import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Container, 
  Typography, 
  Tabs, 
  Tab, 
  Card, 
  CardContent, 
  CardMedia, 
  Grid, 
  Button, 
  Chip, 
  Badge, 
  IconButton, 
  Drawer, 
  List, 
  ListItem, 
  ListItemText, 
  ListItemSecondaryAction, 
  Divider, 
  TextField, 
  Radio, 
  RadioGroup, 
  FormControlLabel, 
  FormControl, 
  FormLabel, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Stepper, 
  Step, 
  StepLabel, 
  Alert,
  Paper,
  CircularProgress,
  AppBar,
  Toolbar,
  useTheme
} from '@mui/material';
import {
  ShoppingCart as CartIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Close as CloseIcon,
  ArrowForward as ArrowForwardIcon,
  Home as HomeIcon,
  Store as StoreIcon,
  LocalDining as DiningIcon,
  ArrowBack as ArrowBackIcon,
  Restaurant as RestaurantIcon
} from '@mui/icons-material';
import MenuService from '../../firebase/MenuService';
import OrderService from '../../firebase/OrderService';
import { ORDER_TYPES } from '../../models/order';

const MenuComponent = ({ viewOnly = false, orderType = null }) => {
  // Hooks
  const navigate = useNavigate();
  const theme = useTheme();
  
  // Estados
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(0);
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [tableNumber, setTableNumber] = useState('');
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    notes: ''
  });
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [orderSuccess, setOrderSuccess] = useState(false);

  // Pasos del checkout
  const checkoutSteps = ['Tu pedido', 'Información personal', 'Pago'];
  // Efecto para cargar datos
  useEffect(() => {
    loadMenuData();
    
    // Si hay un orderType específico, establecer el modo
    if (orderType) {
      // Cargar carrito del localStorage si existe
      const savedCart = localStorage.getItem(`cart_${orderType}`);
      if (savedCart) {
        try {
          setCart(JSON.parse(savedCart));
        } catch (error) {
          console.error('Error parsing saved cart:', error);
        }
      }
      
      // Cargar información del cliente del localStorage si existe
      const savedCustomerInfo = localStorage.getItem('customerInfo');
      if (savedCustomerInfo) {
        try {
          setCustomerInfo(JSON.parse(savedCustomerInfo));
        } catch (error) {
          console.error('Error parsing saved customer info:', error);
        }
      }
    }
  }, [orderType]);

  // Efecto para guardar carrito en localStorage cuando cambia
  useEffect(() => {
    if (orderType && cart.length > 0) {
      localStorage.setItem(`cart_${orderType}`, JSON.stringify(cart));
    }
  }, [cart, orderType]);

  // Efecto para guardar información del cliente en localStorage cuando cambia
  useEffect(() => {
    if (customerInfo.name || customerInfo.email || customerInfo.phone || customerInfo.address) {
      localStorage.setItem('customerInfo', JSON.stringify(customerInfo));
    }
  }, [customerInfo]);

  // Cargar datos del menú desde Firebase
  const loadMenuData = async () => {
    setLoading(true);
    try {
      const menuData = await MenuService.getPublicMenu(orderType);
      
      // Ordenar categorías alfabéticamente
      const sortedCategories = menuData.categories.sort((a, b) => 
        a.name.localeCompare(b.name)
      );
      
      setCategories(sortedCategories);
      setProducts(menuData.products);
      
      // Establecer la primera categoría como seleccionada si hay categorías
      if (sortedCategories.length > 0) {
        setSelectedCategory(sortedCategories[0].id);
      }
      
      setError(null);
    } catch (err) {
      console.error('Error loading menu data:', err);
      setError('Error al cargar los datos del menú. Por favor, inténtalo de nuevo más tarde.');
    } finally {
      setLoading(false);
    }
  };

  // Filtrar productos por la categoría seleccionada
  const filteredProducts = selectedCategory === 0 
    ? products 
    : products.filter(product => product.categoryId === selectedCategory);

  // Calcular total del carrito
  const cartTotal = cart.reduce((total, item) => total + (item.price * item.quantity), 0);

  // Manejar cambio de categoría
  const handleCategoryChange = (event, newValue) => {
    setSelectedCategory(newValue);
  };

  // Añadir producto al carrito
  const addToCart = (product) => {
    const existingItemIndex = cart.findIndex(item => item.id === product.id);
    
    if (existingItemIndex >= 0) {
      // El producto ya está en el carrito, incrementar cantidad
      const updatedCart = [...cart];
      updatedCart[existingItemIndex].quantity += 1;
      setCart(updatedCart);
    } else {
      // Añadir nuevo producto al carrito
      setCart([...cart, { ...product, quantity: 1 }]);
    }
  };

  // Eliminar producto del carrito
  const removeFromCart = (productId) => {
    const existingItemIndex = cart.findIndex(item => item.id === productId);
    
    if (existingItemIndex >= 0) {
      const updatedCart = [...cart];
      if (updatedCart[existingItemIndex].quantity > 1) {
        // Reducir cantidad
        updatedCart[existingItemIndex].quantity -= 1;
      } else {
        // Eliminar del carrito
        updatedCart.splice(existingItemIndex, 1);
      }
      setCart(updatedCart);
    }
  };
  // Iniciar checkout
  const startCheckout = () => {
    setCheckoutOpen(true);
  };

  // Manejar cambio en formulario de cliente
  const handleCustomerInfoChange = (e) => {
    setCustomerInfo({
      ...customerInfo,
      [e.target.name]: e.target.value
    });
  };

  // Avanzar al siguiente paso del checkout
  const handleNext = () => {
    const nextStep = activeStep + 1;
    setActiveStep(nextStep);
    
    if (nextStep === checkoutSteps.length) {
      // Procesar pago y finalizar pedido
      processOrder();
    }
  };

  // Volver al paso anterior del checkout
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  // Procesar pedido
  const processOrder = async () => {
    try {
      // Construir objeto de pedido
      const orderData = {
        type: orderType,
        status: 'pending',
        customer: {
          name: customerInfo.name,
          email: customerInfo.email,
          phone: customerInfo.phone,
          address: orderType === 'delivery' ? customerInfo.address : ''
        },
        items: cart.map(item => ({
          id: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity
        })),
        total: cartTotal,
        table: orderType === 'local' ? `Mesa ${tableNumber}` : '',
        date: new Date().toISOString().split('T')[0],
        time: new Date().toTimeString().split(' ')[0].substring(0, 5),
        notes: customerInfo.notes,
        paymentMethod: 'card', // Por defecto, se puede cambiar
        paymentStatus: 'pending'
      };
      
      // Guardar pedido en Firebase
      await OrderService.createOrder(orderData);
      
      // Vaciar carrito y mostrar mensaje de éxito
      setOrderSuccess(true);
      setCart([]);
      localStorage.removeItem(`cart_${orderType}`);
      
    } catch (error) {
      console.error('Error processing order:', error);
      // Mostrar error al usuario
      setError('Error al procesar el pedido. Por favor, inténtalo de nuevo más tarde.');
    }
  };

  // Cerrar checkout
  const handleCloseCheckout = () => {
    if (orderSuccess) {
      // Si el pedido se completó con éxito, volver a la página principal
      navigate('/');
    } else {
      // Solo cerrar el diálogo
      setCheckoutOpen(false);
      setActiveStep(0);
    }
  };
  // Renderizar paso actual del checkout
  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            {orderType === 'local' && (
              <TextField
                label="Número de mesa"
                value={tableNumber}
                onChange={(e) => setTableNumber(e.target.value)}
                fullWidth
                margin="normal"
                required
                error={!tableNumber}
                helperText={!tableNumber ? "Por favor, introduce el número de mesa" : ""}
              />
            )}
            <List>
              {cart.map((item) => (
                <ListItem key={item.id}>
                  <ListItemText 
                    primary={item.name} 
                    secondary={`${item.quantity} x ${item.price.toFixed(2)}€`} 
                  />
                  <ListItemSecondaryAction>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ mr: 2 }}>
                        {(item.price * item.quantity).toFixed(2)}€
                      </Typography>
                      <IconButton 
                        size="small"
                        onClick={() => removeFromCart(item.id)}
                      >
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                      <Typography sx={{ mx: 1 }}>{item.quantity}</Typography>
                      <IconButton 
                        size="small"
                        onClick={() => addToCart(item)}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body1">Subtotal</Typography>
              <Typography variant="body1">
                {cartTotal.toFixed(2)}€
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body1">IVA (10%)</Typography>
              <Typography variant="body1">
                {(cartTotal * 0.10).toFixed(2)}€
              </Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="subtitle1" fontWeight="bold">TOTAL</Typography>
              <Typography variant="subtitle1" fontWeight="bold">
                {(cartTotal * 1.10).toFixed(2)}€
              </Typography>
            </Box>
          </Box>
        );
      case 1:
        return (
          <Box sx={{ p: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  name="name"
                  label="Nombre completo"
                  value={customerInfo.name}
                  onChange={handleCustomerInfoChange}
                  fullWidth
                  required
                  error={!customerInfo.name}
                  helperText={!customerInfo.name ? "Campo obligatorio" : ""}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="email"
                  label="Email"
                  type="email"
                  value={customerInfo.email}
                  onChange={handleCustomerInfoChange}
                  fullWidth
                  required
                  error={!customerInfo.email}
                  helperText={!customerInfo.email ? "Campo obligatorio" : ""}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="phone"
                  label="Teléfono"
                  value={customerInfo.phone}
                  onChange={handleCustomerInfoChange}
                  fullWidth
                  required
                  error={!customerInfo.phone}
                  helperText={!customerInfo.phone ? "Campo obligatorio" : ""}
                />
              </Grid>
              {orderType === 'delivery' && (
                <Grid item xs={12}>
                  <TextField
                    name="address"
                    label="Dirección de entrega"
                    value={customerInfo.address}
                    onChange={handleCustomerInfoChange}
                    fullWidth
                    required
                    multiline
                    rows={3}
                    error={!customerInfo.address}
                    helperText={!customerInfo.address ? "Campo obligatorio para entrega a domicilio" : ""}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField
                  name="notes"
                  label="Notas adicionales"
                  value={customerInfo.notes}
                  onChange={handleCustomerInfoChange}
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="Alergias, preferencias, etc."
                />
              </Grid>
            </Grid>
          </Box>
        );
      case 2:
        return (
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Método de pago
            </Typography>
            <FormControl component="fieldset">
              <RadioGroup defaultValue="card">
                <FormControlLabel 
                  value="card" 
                  control={<Radio />} 
                  label="Tarjeta de crédito/débito" 
                />
                <FormControlLabel 
                  value="paypal" 
                  control={<Radio />} 
                  label="PayPal" 
                />
                {(orderType === 'local' || orderType === 'takeaway') && (
                  <FormControlLabel 
                    value="cash" 
                    control={<Radio />} 
                    label="Efectivo" 
                  />
                )}
              </RadioGroup>
            </FormControl>
            <Box sx={{ mt: 3, p: 2, bgcolor: theme.palette.neutral.main, borderRadius: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Resumen del pedido
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Productos ({cart.length})</Typography>
                <Typography variant="body2">{cartTotal.toFixed(2)}€</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">IVA (10%)</Typography>
                <Typography variant="body2">{(cartTotal * 0.10).toFixed(2)}€</Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2">Total a pagar</Typography>
                <Typography variant="subtitle2">{(cartTotal * 1.10).toFixed(2)}€</Typography>
              </Box>
            </Box>
          </Box>
        );
      default:
        return 'Unknown step';
    }
  };
  // Renderizar barra superior
  const renderAppBar = () => (
    <AppBar 
      position="static" 
      color="default" 
      elevation={0}
      sx={{ 
        bgcolor: theme.palette.background.default, 
        borderBottom: `1px solid ${theme.palette.divider}`,
        mb: 3
      }}
    >
      <Toolbar>
        {!viewOnly && (
          <IconButton 
            edge="start" 
            color="inherit" 
            onClick={() => navigate('/')}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
        )}
        <RestaurantIcon sx={{ mr: 2, color: theme.palette.primary.main }} />
        <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontFamily: theme.typography.h6.fontFamily }}>
          {viewOnly 
            ? "Nuestra Carta" 
            : `Pedido ${ORDER_TYPES[orderType.toUpperCase()]?.label}`}
        </Typography>
        {!viewOnly && (
          <IconButton 
            color="primary" 
            onClick={() => setCartOpen(true)}
            disabled={cart.length === 0}
          >
            <Badge badgeContent={cart.length} color="error">
              <CartIcon />
            </Badge>
          </IconButton>
        )}
      </Toolbar>
    </AppBar>
  );

  // Si está cargando
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Si hay error
  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {renderAppBar()}
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button 
          variant="contained" 
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/')}
        >
          Volver al inicio
        </Button>
      </Container>
    );
  }
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {renderAppBar()}

      {/* Si no estamos en modo visualización, mostrar selector de pedido */}
      {viewOnly && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
          <Button 
            variant="contained"
            color="primary"
            onClick={() => navigate('/pedido-mesa')}
            startIcon={<DiningIcon />}
            sx={{ mx: 1 }}
          >
            Pedir en Mesa
          </Button>
          <Button 
            variant="contained"
            color="secondary"
            onClick={() => navigate('/pedido-recoger')}
            startIcon={<StoreIcon />}
            sx={{ mx: 1 }}
          >
            Para Recoger
          </Button>
          <Button 
            variant="contained"
            color="primary"
            onClick={() => navigate('/pedido-domicilio')}
            startIcon={<HomeIcon />}
            sx={{ mx: 1 }}
          >
            A Domicilio
          </Button>
        </Box>
      )}

      {/* Pestañas de Categorías */}
      <Paper 
        elevation={0} 
        sx={{ 
          borderRadius: 3, 
          mb: 3, 
          bgcolor: theme.palette.neutral.main,
          border: `1px solid ${theme.palette.divider}`
        }}
      >
        <Tabs
          value={selectedCategory}
          onChange={handleCategoryChange}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
          sx={{ 
            px: 2,
            '& .MuiTab-root': {
              fontWeight: 500,
              minHeight: 48,
              textTransform: 'none',
              fontSize: '1rem',
            }
          }}
        >
          {categories.map((category) => (
            <Tab key={category.id} label={category.name} value={category.id} />
          ))}
        </Tabs>
      </Paper>

      {/* Lista de Productos */}
      <Grid container spacing={3}>
        {filteredProducts.length > 0 ? (
          filteredProducts.map((product) => (
            <Grid item xs={12} sm={6} md={4} key={product.id}>
              <Card 
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0px 6px 12px rgba(0, 0, 0, 0.15)'
                  }
                }}
              >
                <CardMedia
                  component="img"
                  height="160"
                  image={product.image || '/api/placeholder/300/200'}
                  alt={product.name}
                  sx={{ objectFit: 'cover' }}
                />
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Typography gutterBottom variant="h6" component="div" sx={{ fontWeight: 600 }}>
                      {product.name}
                    </Typography>
                    <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
                      {product.price.toFixed(2)}€
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {product.description}
                  </Typography>
                  {!viewOnly && (
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => addToCart(product)}
                      sx={{ 
                        mt: 'auto',
                        bgcolor: theme.palette.primary.main,
                        color: 'white',
                        '&:hover': {
                          bgcolor: theme.palette.primary.dark
                        }
                      }}
                      fullWidth
                    >
                      Añadir
                    </Button>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))
        ) : (
          <Grid item xs={12}>
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="textSecondary">
                No hay productos disponibles en esta categoría
              </Typography>
            </Paper>
          </Grid>
        )}
      </Grid>
      {/* Drawer del Carrito */}
      {!viewOnly && (
        <Drawer
          anchor="right"
          open={cartOpen}
          onClose={() => setCartOpen(false)}
        >
          <Box sx={{ width: 350, p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Tu Pedido</Typography>
              <IconButton onClick={() => setCartOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
            {cart.length > 0 ? (
              <>
                <List>
                  {cart.map((item) => (
                    <ListItem key={item.id}>
                      <ListItemText
                        primary={item.name}
                        secondary={`${item.price.toFixed(2)}€`}
                      />
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton 
                          size="small"
                          onClick={() => removeFromCart(item.id)}
                        >
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                        <Typography sx={{ mx: 1 }}>{item.quantity}</Typography>
                        <IconButton 
                          size="small"
                          onClick={() => addToCart(item)}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </ListItem>
                  ))}
                </List>
                <Divider />
                <Box sx={{ mt: 2, mb: 2, display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="h6">Total:</Typography>
                  <Typography variant="h6">{cartTotal.toFixed(2)}€</Typography>
                </Box>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  startIcon={<ArrowForwardIcon />}
                  onClick={() => {
                    setCartOpen(false);
                    startCheckout();
                  }}
                  sx={{
                    bgcolor: theme.palette.primary.main,
                    '&:hover': {
                      bgcolor: theme.palette.primary.dark
                    }
                  }}
                >
                  Continuar con el Pedido
                </Button>
              </>
            ) : (
              <Typography variant="body1" sx={{ textAlign: 'center', mt: 4 }}>
                Tu carrito está vacío
              </Typography>
            )}
          </Box>
        </Drawer>
      )}

      {/* Diálogo de Checkout */}
      {!viewOnly && (
        <Dialog
          open={checkoutOpen}
          onClose={handleCloseCheckout}
          fullWidth
          maxWidth="sm"
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.15)'
            }
          }}
        >
          {orderSuccess ? (
            <DialogContent>
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Alert severity="success" sx={{ mb: 3 }}>
                  ¡Pedido realizado con éxito!
                </Alert>
                <Typography variant="h6" gutterBottom>
                  Gracias por tu pedido
                </Typography>
                <Typography variant="body1" paragraph>
                  Hemos recibido tu pedido y estamos procesándolo.
                  {orderType === 'delivery' 
                    ? ' Te lo entregaremos lo antes posible.' 
                    : orderType === 'takeaway'
                      ? ' Te avisaremos cuando esté listo para recoger.'
                      : ' Lo serviremos en tu mesa en breve.'}
                </Typography>
                <Button
                  variant="contained"
                  onClick={handleCloseCheckout}
                  sx={{ 
                    mt: 2,
                    bgcolor: theme.palette.primary.main,
                    '&:hover': {
                      bgcolor: theme.palette.primary.dark
                    }
                  }}
                >
                  Cerrar
                </Button>
              </Box>
            </DialogContent>
          ) : (
            <>
              <DialogTitle sx={{ 
                bgcolor: theme.palette.primary.main, 
                color: 'white',
                px: 3,
                py: 2
              }}>
                Finalizar Pedido - {
                  orderType === 'local' ? 'En Mesa' : 
                  orderType === 'takeaway' ? 'Para Recoger' : 'A Domicilio'
                }
              </DialogTitle>
              <DialogContent sx={{ px: 3, py: 4 }}>
                <Stepper activeStep={activeStep} sx={{ pt: 2, pb: 4 }}>
                  {checkoutSteps.map((label) => (
                    <Step key={label}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
                {getStepContent(activeStep)}
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button 
                  onClick={handleCloseCheckout}
                  variant="outlined"
                >
                  Cancelar
                </Button>
                <Button
                  disabled={activeStep === 0 && cart.length === 0}
                  onClick={handleBack}
                  sx={{ display: activeStep === 0 ? 'none' : 'inline-flex' }}
                  variant="outlined"
                >
                  Atrás
                </Button>
                <Button 
                  variant="contained" 
                  onClick={handleNext}
                  disabled={
                    (activeStep === 0 && (orderType === 'local' && !tableNumber)) ||
                    (activeStep === 1 && 
                     (customerInfo.name === '' || 
                      customerInfo.email === '' || 
                      customerInfo.phone === '' || 
                      (orderType === 'delivery' && customerInfo.address === '')
                     ))
                  }
                  sx={{
                    bgcolor: theme.palette.primary.main,
                    '&:hover': {
                      bgcolor: theme.palette.primary.dark
                    }
                  }}
                >
                  {activeStep === checkoutSteps.length - 1 ? 'Confirmar Pedido' : 'Siguiente'}
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      )}
    </Container>
  );
};

export default MenuComponent;